/**
 * Learning Module media catalog (ITE-4840).
 * fileName + fileType must match objects in the Learning GCP bucket:
 *   manuals/{fileName}.pdf|jpg | videos/{fileName}.mp4
 *
 * Care: Care+ training list (screenshot / AC updates — add, replace, rename).
 * Standard (advanced): existing bucket filenames so non-care links keep working.
 */
export type LearningFileType = 'pdf' | 'mp4' | 'jpg';

export interface LearningDocItem {
	title: string;
	fileName: string;
	fileType: LearningFileType;
	automationId: string;
}

export interface LearningSection {
	title: string;
	automationId: string;
	items: LearningDocItem[];
}

/**
 * Care company learning sections (Care+).
 * Green = new, purple = replace with new GCS object, orange = keep + blue rename.
 */
export const CARE_LEARNING_SECTIONS: LearningSection[] = [
	{
		title: $localize`:@@learningTab-section-appAndCodeAccess:App and Code Access`,
		automationId: 'app-code-access-care-learning-title',
		items: [
			{
				title: 'App User Guide',
				fileName: 'Access+ Care - App User Guide',
				fileType: 'pdf',
				automationId: 'learning-doc-access-plus-app-guide-button',
			},
			{
				title: 'App Video Tutorial',
				fileName: 'Access+ Care - App Video Tutorial',
				fileType: 'mp4',
				automationId: 'learning-doc-access-plus-app-video-button',
			},
			{
				title: 'App Download QR Codes',
				fileName: 'Access+ Care - App Download QR Codes',
				fileType: 'jpg',
				automationId:
					'learning-doc-code-access-plus-app-download-qr-codes-button',
			},
			{
				title: 'Code Access Guide',
				fileName: 'Access+ Care - Code-Access - SentriKey and SentriGuard',
				fileType: 'jpg',
				automationId: 'learning-doc-code-access-plus-code-access-guide-button',
			},
			{
				title: 'Best Practices',
				fileName: 'Access+ Care - Best Practices',
				fileType: 'pdf',
				automationId:
					'learning-doc-mobile-app-access-plus-best-practices-button',
			},
		],
	},
	{
		title: $localize`:@@learningTab-section-platformGuidesForCareProviders:Platform Guides for Care Providers`,
		automationId: 'platform-guide-for-care-providers-care-learning-title',
		items: [
			{
				title: 'Platform Overview Video',
				fileName: 'Care on Access+ platform for care providers',
				fileType: 'mp4',
				automationId: 'learning-doc-platform-overview-video-button',
			},
			{
				title: 'Platform Guide for Care Providers',
				fileName: 'Access+ Care - Platform Guide for Care Providers',
				fileType: 'pdf',
				automationId: 'learning-doc-platform-guide-for-care-providers-button',
			},
		],
	},
];

/**
 * Standard (advanced / non-care) learning sections.
 * Keeps working Mobile App / Platform Manual GCS names; adds Standard AC
 * QR / Code Access JPG / Whitelisting / install steps / Battery video,
 * and replaces Installation Manual object per AC.
 */
export const STANDARD_LEARNING_SECTIONS: LearningSection[] = [
	{
		title: $localize`:@@learningTab-section-appAndCodeAccess:App and Code Access`,
		automationId: 'app-code-access-standard-learning-title',
		items: [
			{
				title: 'Mobile App for SentriGuard(R) Guide',
				fileName: 'Access+ App for SentriGuard Guide - 200526',
				fileType: 'pdf',
				automationId:
					'learning-doc-app-and-code-access-app-for-sentriguard-guide-button',
			},
			{
				title: 'Access+ app Download QR Codes',
				fileName: 'Access+ App Download QR codes',
				fileType: 'jpg',
				automationId:
					'learning-doc-app-and-code-access-app-download-qr-codes-button',
			},
			{
				title: 'Code Access',
				fileName: 'Code-Access-SentriGuard',
				fileType: 'jpg',
				automationId:
					'learning-doc-app-and-code-access-code-access-guide-button',
			},
			{
				title: 'Code Access - Video Tutorial',
				fileName: 'Code Access',
				fileType: 'mp4',
				automationId:
					'learning-doc-app-and-code-access-code-access-video-button',
			},
			{
				title: 'Mobile App Access - Video Tutorial',
				fileName: 'Mobile App Access',
				fileType: 'mp4',
				automationId:
					'learning-doc-app-and-code-access-mobile-app-access-video-button',
			},
		],
	},
	{
		title: $localize`:@@learningTab-section-platformGuides:Platform Guides`,
		automationId: 'platform-guides-standard-learning-title',
		items: [
			{
				title: 'Platform Manual',
				fileName: 'Platform Manual',
				fileType: 'pdf',
				automationId: 'learning-doc-platform-guides-platform-manual-button',
			},
			{
				title: 'User Management - Video Tutorial',
				fileName: 'User Management',
				fileType: 'mp4',
				automationId:
					'learning-doc-platform-guides-user-management-video-button',
			},
			{
				title: 'Creating Roles - Video Tutorial',
				fileName: 'Creating Roles',
				fileType: 'mp4',
				automationId:
					'learning-doc-platform-guides-creating-roles-video-button',
			},
			{
				title: 'Property Management - Video Tutorial',
				fileName: 'Property Management',
				fileType: 'mp4',
				automationId:
					'learning-doc-platform-guides-property-management-video-button',
			},
			{
				title: 'Device Management - Video Tutorial',
				fileName: 'Device Management',
				fileType: 'mp4',
				automationId:
					'learning-doc-platform-guides-device-management-video-button',
			},
			{
				title: 'Company Management - Video Tutorial',
				fileName: 'Company Management',
				fileType: 'mp4',
				automationId:
					'learning-doc-platform-guides-company-management-video-button',
			},
			{
				title: 'Generating Reports - Video Tutorial',
				fileName: 'Generating Reports',
				fileType: 'mp4',
				automationId:
					'learning-doc-platform-guides-generating-reports-video-button',
			},
			{
				title: 'Whitelisting',
				fileName: 'Whitelisting - 11MB',
				fileType: 'mp4',
				automationId: 'learning-doc-platform-guides-whitelisting-video-button',
			},
			{
				title: 'Starter Guide – Step-by-Step on Access+ Platform',
				fileName: 'Starter Guide_ Step-by-step Access+ platform - 050626',
				fileType: 'pdf',
				automationId:
					'learning-doc-platform-guides-starter-guide-access-plus-button',
			},
		],
	},
	{
		title: $localize`:@@learningTab-section-installationAndDeviceMaintenance:Installation and Device Maintenance`,
		automationId: 'installation-device-maintenance-standard-learning-title',
		items: [
			{
				title: 'Installation Manual',
				fileName: 'SentriGuard Key Safe Installation Manual - 2025 v2 - 151025',
				fileType: 'pdf',
				automationId:
					'learning-doc-installation-and-device-maintenance-installation-manual-button',
			},
			{
				title: 'Installation steps on the platform',
				fileName: 'Installation steps on the platform - SentriKey Access+',
				fileType: 'pdf',
				automationId:
					'learning-doc-installation-and-device-maintenance-installation-steps-on-the-platform-button',
			},
			{
				title: 'Installing SentriGuard - Video Tutorial',
				fileName: 'Installing SentriGuard',
				fileType: 'mp4',
				automationId:
					'learning-doc-installation-and-device-maintenance-installing-sentriguard-video-button',
			},
			{
				title: 'Uninstalling SentriGuard - Video Tutorial',
				fileName: 'Uninstalling SentriGuard',
				fileType: 'mp4',
				automationId:
					'learning-doc-installation-and-device-maintenance-uninstalling-sentriguard-video-button',
			},
			{
				title: 'Battery Replacement – Video Tutorial',
				fileName: 'Battery Replacement - SentriGuard',
				fileType: 'mp4',
				automationId:
					'learning-doc-installation-and-device-maintenance-battery-replacement-video-button',
			},
			{
				title: 'Battery Replacement Guide',
				fileName: 'SentriGuard battery replacement guide - v2',
				fileType: 'pdf',
				automationId:
					'learning-doc-installation-and-device-maintenance-battery-replacement-guide-button',
			},
		],
	},
];

/** Non-care (advanced, undefined, or any other type) uses Standard catalog. */
export function learningSectionsForCompanyType(
	companyType: 'advanced' | 'care' | string | undefined | null
): LearningSection[] {
	return companyType === 'care'
		? CARE_LEARNING_SECTIONS
		: STANDARD_LEARNING_SECTIONS;
}

/** Expected GCS object paths for upload checklist (dev/staging/prod Learning buckets). */
export const LEARNING_GCS_OBJECT_PATHS: string[] = [
	...CARE_LEARNING_SECTIONS,
	...STANDARD_LEARNING_SECTIONS,
]
	.flatMap((section) => section.items)
	.map((item) => {
		const folder = item.fileType === 'mp4' ? 'videos' : 'manuals';
		return `${folder}/${item.fileName}.${item.fileType}`;
	})
	.filter((path, index, all) => all.indexOf(path) === index);
